import { useState, useEffect, useContext, Fragment } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useParams, useHistory, Link } from 'react-router-dom';
import { Grid, Box, FormControl, FormLabel, FormGroup, FilledInput, InputAdornment, IconButton, Icon, Divider, Tooltip, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import dayjs from 'dayjs';

import Loader from '../../common/loader';
import NotFound from '../../common/not-found';

import AuthContext from '../../context';
import { get, put, del } from '../../utils/api-services';
import helpers from '../../utils/helpers';
import themeColors from '../../assets/colors';

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: 20
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    delete: {
        width: '100%',
        backgroundColor: '#dc3545',
        borderColor: '#dc3545',
        '&:hover': {
            backgroundColor: '#dc3545',
            borderColor: '#dc3545',
            boxShadow: 'none',
        },
    },
    divider: {
        marginBottom: 24
    },
    assignContainer: {
        marginTop: 24
    },
    row: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    rowText: {
        display: 'inherit',
        flexDirection: 'inherit',
        alignItems: 'inherit'
    },
    text: {
        fontSize: '1rem',
        marginLeft: 5,
        marginRight: 10,
        color: themeColors.gray[600]
    },
    detail: {
        backgroundColor: themeColors.white.main
    },
    textContainer: {
        fontSize: 10,
        color: '#000'
    },
    tableHeader: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        padding: 8,
        backgroundColor: '#F2F2F2'
    },
    total: {
        backgroundColor: '#E0F8E0'
    },
    footer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        backgroundColor: '#283851',
        color: '#fff',
        padding: 8,
        pageBreakAfter: 'always'
    },
    error: {
        padding: 15,
        backgroundColor: themeColors.visible['false']
    }
}));

const Text = ({ title }) => {
    const classes = useStyles();

    return <TableCell align='center' className={classes.textContainer}>{title}</TableCell>;
}

const ChecksDetail = ({ permissions }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const { id } = useParams();
    const history = useHistory();
    const classes = useStyles();
    const [state, setState] = useState({
        loading: true,
        check: {},
        original: {},
        tickets: [],
        assignTickets: [],
        permissions: {}
    });

    useEffect(() => {
        setState(prev => ({ ...prev, permissions }));
    }, [permissions]);

    useEffect(() => {
        window.document.title = 'Payment - Detail';

        makeRequest();
    }, []);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get(`/check/${id}`);
        if (response.status === 200) {
            const sede = JSON.parse(localStorage.getItem('@sede'));
            if (response?.data?.status !== 'active' || response?.data?.idsede !== sede.id) {
                showToast('This payment is not available', 'error');
                history.replace('/ticket/checks');
            } else {
                setState(prev => ({
                    ...prev,
                    loading: false,
                    check: response?.data ?? {},
                    original: response?.data ?? {},
                    tickets: response?.data?.tickets ?? []
                }));
            }
        } else {
            console.log('[ChecksDetail] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const validateForm = type => {
        const { update_attr, delete_attr } = state.permissions;

        switch (type) {
            case 'code':
            case 'invoice_number':
                if (!update_attr) {
                    showToast('You have no permissions for this action', 'error', 6500);
                    return false;
                }
                const before = JSON.stringify({ [type]: state.original[type]?.trim() });
                const after = JSON.stringify({ [type]: state.check[type]?.trim() });
                if (before === after) {
                    showToast('Update some field', 'error');
                    return false;
                }
                if (!state.check[type]?.trim()) {
                    showToast('The field is required', 'error');
                    return false;
                }

                return true;
            case 'delete':
                if (!delete_attr) {
                    showToast('You have no permissions for this action', 'error', 6500);
                    return false;
                }

                return window.confirm('Are you sure you want to delete this payment?');
            default:
                break;
        }
    }

    const update = async (column) => {
        const cond = validateForm(column);

        if (cond) {
            showLoader(true);
            const response = await put(`/check/${id}/update`, { column, [column]: state.check[column] });
            if (response.status === 200) {
                setState(prev => ({
                    ...prev,
                    check: response.data,
                    original: response.data,
                    tickets: response.data?.tickets ?? []
                }));
                showToast(response.message);
            } else {
                console.log('[ChecksDetail] update error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const destroy = async () => {
        const cond = validateForm('delete');

        if (cond) {
            showLoader(true);
            const response = await del(`/check/${id}`);
            if (response.status === 200) {
                showToast(response.message);
                history.replace('/ticket/checks');
            } else {
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const handleChange = ({ target }) => setState(prev => ({ ...prev, check: { ...prev.check, [target.name]: target.value } }));

    const getTotal = filter => {
        let total = { qty: 0, subtotal: 0, stumpage: 0, fee: 0, extra: 0, total: 0 };
        let freight = 0;

        filter.tickets.forEach(ticket => {
            ticket.detail.forEach(detail => {
                if (detail.item.action === '+') {
                    total.qty = Number(total.qty) + (detail.qty * detail.qty_product);
                    total.subtotal = (Number(total.subtotal) + Number(detail.subtotal)).toFixed(2);
                    total.stumpage = (Number(total.stumpage) + Number(detail.stumpage)).toFixed(2);
                    total.fee = (Number(total.fee) + Number(detail.admin_fee)).toFixed(2);
                    total.extra = (Number(total.extra) + Number(detail.extras)).toFixed(2);
                    total.total = (Number(total.total) + Number(detail.total)).toFixed(2);
                } else if (detail.item.action === '-') {
                    total.qty = Number(total.qty) - (detail.qty * detail.qty_product);
                    total.subtotal = (Number(total.subtotal) - Number(detail.subtotal)).toFixed(2);
                    total.stumpage = (Number(total.stumpage) - Number(detail.stumpage)).toFixed(2);
                    total.fee = (Number(total.fee) - Number(detail.admin_fee)).toFixed(2);
                    total.extra = (Number(total.extra) - Number(detail.extras)).toFixed(2);
                    total.total = (Number(total.total) - Number(detail.total)).toFixed(2);
                }

                freight = (Number(freight) + Number(detail.freight)).toFixed(2);
            });
        });

        const values = [];
        for (const key in total) {
            switch (key) {
                case 'qty':
                    values.push(total[key]);
                    break;
                case 'stumpage':
                    values.push(`- $ ${total[key]}`);
                    break;
                case 'fee':
                    values.push(`- $ ${total[key]}`);
                    break;
                case 'extra':
                    values.push(`+ $ ${total[key]}`);
                    break;
                default:
                    values.push(`$ ${total[key]}`);
                    break;
            }
        }

        if (filter.use_freight) values.push(`- $ ${freight}`);

        return values;
    }

    const getFullTotal = check => {
        const { sales, filters } = check;

        let sales_subtotal = '0';
        let qty = '0';
        let subtotal = '0';
        let stumpage = '0';
        let admin_fee = '0';
        let extras = '0';
        let total = '0';
        let freight = '0';
        let use_freight = false;

        sales.forEach(sale => sales_subtotal = (Number(sales_subtotal) + Number(sale.total)).toFixed(2));

        filters.forEach(filter => {
            if (filter.use_freight) use_freight = true;

            filter.tickets.forEach(ticket => {
                ticket.detail.forEach(detail => {
                    if (detail.item.action === '+') {
                        qty = Number(qty) + Number(detail.total_qty);
                        subtotal = (Number(subtotal) + Number(detail.subtotal)).toFixed(2);
                        stumpage = (Number(stumpage) + Number(detail.stumpage)).toFixed(2);
                        admin_fee = (Number(admin_fee) + Number(detail.admin_fee)).toFixed(2);
                        extras = (Number(extras) + Number(detail.extras)).toFixed(2);
                        total = (Number(total) + Number(detail.total)).toFixed(2);
                    } else if (detail.item.action === '-') {
                        qty = Number(qty) - Number(detail.total_qty);
                        subtotal = (Number(subtotal) - Number(detail.subtotal)).toFixed(2);
                        stumpage = (Number(stumpage) - Number(detail.stumpage)).toFixed(2);
                        admin_fee = (Number(admin_fee) - Number(detail.admin_fee)).toFixed(2);
                        extras = (Number(extras) - Number(detail.extras)).toFixed(2);
                        total = (Number(total) - Number(detail.total)).toFixed(2);
                    }

                    freight = (Number(freight) + Number(detail.freight)).toFixed(2);
                });
            });
        });
        total = (Number(total) - Number(freight) - Number(sales_subtotal)).toFixed(2);

        const totals = [`Sales: $ ${sales_subtotal}`, `Qty: ${qty}`, `Subtotal: $ ${subtotal}`, `Stumpage: $ ${stumpage}`, `Admin fee: $ ${admin_fee}`, `Extras: $ ${extras}`];
        (use_freight) ? totals.push(`Freight: $ ${freight}`, `Total: $ ${total}`) : totals.push(`Total: $ ${total}`);

        return totals;
    }

    if (state.loading) return <Loader fullScreen />;
    if (!state.permissions.read_attr || !state.check.idcheck) return <NotFound />;

    return (
        <div className={classes.root}>
            <Divider />
            <div className={classes.row}>
                <div className={classes.rowText}>
                    <h3>{'Status:'}</h3>
                    <span className={classes.text}>{state.check.state}</span>
                </div>
            </div>
            <Divider className={classes.divider} />

            <Grid container>
                <Grid item xs={6} lg={3} xl={2}>
                    <FormGroup>
                        <FormLabel>{'Supplier'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                autoComplete='off'
                                type='text'
                                placeholder='Supplier'
                                name='supplier'
                                value={state.check.supplier.name}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={3} xl={2}>
                    <FormGroup>
                        <FormLabel>{'Week'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                autoComplete='off'
                                type='text'
                                placeholder='Week'
                                name='week'
                                value={state.check.week}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>

                <Grid item xs={6} lg={3} xl={2}>
                    <FormGroup>
                        <FormLabel>{'Payment No.'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='text'
                                placeholder='Payment No.'
                                name='code'
                                value={state.check?.code ?? ''}
                                onChange={handleChange}
                                endAdornment={
                                    (state.permissions.update_attr) ?
                                        <InputAdornment position='end'>
                                            <Tooltip title='Edit' placement='top'>
                                                <IconButton size='small' onClick={() => update('code')}>
                                                    <Icon fontSize='small'>{'edit'}</Icon>
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment> :
                                        null
                                }
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={3} xl={2}>
                    <FormGroup>
                        <FormLabel>{'Invoice No.'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='text'
                                placeholder='Invoice No.'
                                name='invoice_number'
                                value={state.check?.invoice_number ?? ''}
                                onChange={handleChange}
                                endAdornment={
                                    (state.permissions.update_attr) ?
                                        <InputAdornment position='end'>
                                            <Tooltip title='Edit' placement='top'>
                                                <IconButton size='small' onClick={() => update('invoice_number')}>
                                                    <Icon fontSize='small'>{'edit'}</Icon>
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment> :
                                        null
                                }
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={3} xl={2}>
                    <FormGroup>
                        <FormLabel>{'Sede'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                autoComplete='off'
                                type='text'
                                placeholder='Sede'
                                name='sede'
                                value={state.check?.sede?.name}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                {
                    state.permissions?.delete_attr &&
                    <Grid item xs={6} lg={2} xl={1} className={classes.buttonContainer}>
                        <Button
                            variant='contained'
                            className={classes.delete}
                            onClick={destroy}
                        >
                            {'Delete'}
                        </Button>
                    </Grid>
                }
            </Grid>

            {
                (!Boolean(state.original?.code) || !Boolean(state.original?.invoice_number)) &&
                <pre className={classes.error}>
                    {'The payment number or the invoice number cannot be empty.'}
                </pre>
            }
            {
                (state.check.error) &&
                <pre className={classes.error}>
                    {state.check?.error_msg}
                </pre>
            }

            <Divider />
            <div className={classes.row}>
                <h2>{'Detail'}</h2>
                <h2>{`${state.check.cash_payment ? 'Cash' : 'Check'} payment`}</h2>
                <div className={classes.rowText}>
                    <h2>{'Total:'}</h2>
                    <label className={classes.text}>{`$ ${state.check.total}`}</label>
                </div>
            </div>
            <Divider className={classes.divider} />

            {
                state.check.filters.map((filter, i) =>
                    <div key={i.toString()} className={classes.tableContainer}>
                        <div className={classes.tableHeader}>
                            <span><b>{`Date: `}</b>{dayjs(filter.date).format('MM/DD/YYYY')}</span>
                            <span><b>{`Location: `}</b>{filter.location.name}</span>
                            <span><b>{`Land: `}</b>{filter.land.name}</span>
                        </div>

                        <TableContainer component={Box}>
                            <Table size='small' className={classes.detail}>
                                <TableHead>
                                    <TableRow>
                                        <Text title={'Master'} />
                                        <Text title={'Breakdown'} />
                                        <Text title={'Item'} />
                                        <Text title={'Product'} />
                                        <Text title={'Type'} />
                                        <Text title={'Price'} />
                                        <Text title={'Qty'} />
                                        <Text title={'Subtotal'} />
                                        <Text title={'Stumpage'} />
                                        <Text title={'Admin Fee'} />
                                        <Text title={'Extras'} />
                                        <Text title={'Total'} />
                                        {
                                            filter.use_freight &&
                                            <Text title={'Freight'} />
                                        }
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        filter.tickets.map(ticket =>
                                            <Fragment key={ticket.idticket}>
                                                {
                                                    ticket.detail.map((detail, j) =>
                                                        <TableRow key={detail.iddetail_ticket}>
                                                            <TableCell align='center' className={classes.textContainer}>
                                                                <Link to={`/ticket/tickets/master/${ticket?.master?.idticket_master ?? '?'}`} target='_blank'>
                                                                    {(j === 0) ? ticket?.master?.code ?? '?' : '"'}
                                                                </Link>
                                                            </TableCell>
                                                            <TableCell align='center' className={classes.textContainer}>
                                                                <Link to={`/ticket/tickets/${ticket?.idticket ?? '?'}`} target='_blank'>
                                                                    {(j === 0) ? ticket?.code ?? '?' : '"'}
                                                                </Link>
                                                            </TableCell>
                                                            <Text title={detail.item.name} />
                                                            <Text title={detail?.product_type_ref?.product.name ?? '?'} />
                                                            <Text title={detail?.product_type_ref?.type.name ?? '?'} />
                                                            <Text title={`$ ${detail.final_price}`} />
                                                            <Text title={detail.qty * detail.qty_product} />
                                                            <Text title={`$ ${detail.subtotal}`} />
                                                            <Text title={`- $ ${detail.stumpage}`} />
                                                            <Text title={`- $ ${detail.admin_fee}`} />
                                                            <Text title={`+ $ ${detail.extras}`} />
                                                            <Text title={`$ ${detail.total}`} />
                                                            {
                                                                (filter.use_freight) &&
                                                                <Text title={`$ ${detail.freight}`} />
                                                            }
                                                        </TableRow>
                                                    )
                                                }
                                            </Fragment>
                                        )
                                    }
                                    <TableRow className={classes.total}>
                                        <TableCell className={classes.text} align='center'>{'TOTAL:'}</TableCell>
                                        <TableCell colSpan={5} />
                                        {
                                            getTotal(filter).map((item, i) => <Text key={i.toString()} title={`${item}`} />)
                                        }
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                )
            }
            {
                (state.check?.sales?.length > 0) &&
                <div className={classes.tableContainer}>
                    <h3>{'Sales'}</h3>
                    <TableContainer component={Box}>
                        <Table size='small' className={classes.detail}>
                            <TableHead>
                                <TableRow>
                                    <Text title={'Code'} />
                                    <Text title={'Date'} />
                                    <Text title={'Product'} />
                                    <Text title={'Type'} />
                                    <Text title={'Price'} />
                                    <Text title={'Qty'} />
                                    <Text title={'Total'} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    state.check.sales.map(sale => {
                                        return (
                                            <TableRow key={sale.idticket_sale}>
                                                <TableCell align='center' className={classes.textContainer}>
                                                    <Link to={`/ticket/ticketSale/${sale.idticket_sale}`} target='_blank'>
                                                        {sale.code}
                                                    </Link>
                                                </TableCell>
                                                <Text title={dayjs(sale.date).format('MM/DD/YYYY')} />
                                                <Text title={sale.product_type_ref.product.name} />
                                                <Text title={sale.product_type_ref.type.name} />
                                                <Text title={`$ ${sale.price}`} />
                                                <Text title={(sale.qty * sale.product_qty)} />
                                                <Text title={`$ ${sale.total}`} />
                                            </TableRow>
                                        );
                                    })
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            }
            <div className={classes.footer}>
                {
                    getFullTotal(state.check).map(
                        (item, i) => <span key={i.toString()}>{item}</span>
                    )
                }
            </div>

        </div >
    );
}

export default ChecksDetail;